import { useState, useEffect } from "react";
import {
  getUsers,
  addAnnotator,
  removeAnnotator,
  reactivateAnnotator,
  getChunkPriorityScore,
  updateChunkExtraScore,
} from "../services/api";
import Sidebar from "./Sidebar";
import ConfirmModal from "./ConfirmModal";
import { useEventLogger } from '../hooks/useEventLogger';

export default function UserManagement({ user, onPageChange }) {
  const { logNavigation, logLogout } = useEventLogger(user);
  const [users, setUsers] = useState({
    admins: [],
    annotators: [],
    inactive_annotators: [],
  });
  const [newEmail, setNewEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    email: "",
  });
  const [chunkObjectId, setChunkObjectId] = useState("");
  const [chunkPriority, setChunkPriority] = useState(null);
  const [chunkExtraScore, setChunkExtraScore] = useState("");
  const [chunkStatus, setChunkStatus] = useState("");
  const [chunkLoading, setChunkLoading] = useState(false);

  // Email validation regex
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const validateEmail = (email) => {
    return emailRegex.test(email);
  };

  // Auto-hide error after 3 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (err) {
      if (err.response?.status === 403) {
        // User is not admin, redirect to dashboard
        onPageChange("dashboard");
        return;
      }
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleAddAnnotator = async (e) => {
    e.preventDefault();
    if (!newEmail) return;

    // Validate email format
    if (!validateEmail(newEmail)) {
      setError("Please enter a valid email address");
      return;
    }

    try {
      await addAnnotator(newEmail);
      setNewEmail("");
      setError(""); // Clear any previous errors
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add user");
    }
  };

  const handleRemoveAnnotator = async (email) => {
    setConfirmModal({ isOpen: true, email });
  };

  const confirmRemove = async () => {
    try {
      await removeAnnotator(confirmModal.email);
      loadUsers();
      setConfirmModal({ isOpen: false, email: "" });
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to deactivate user");
    }
  };

  const handleReactivate = async (email) => {
    try {
      await reactivateAnnotator(email);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reactivate user");
    }
  };

  const handleLoadChunkScore = async () => {
    if (!chunkObjectId.trim()) {
      setChunkStatus("Please enter a MongoDB chunk id");
      return;
    }

    setChunkLoading(true);
    setChunkStatus("");
    try {
      const data = await getChunkPriorityScore(chunkObjectId.trim());
      setChunkPriority(data);
      setChunkExtraScore(data.extra_score ?? "");
      setChunkStatus("Loaded chunk score");
    } catch (err) {
      setChunkPriority(null);
      setChunkStatus(err.response?.data?.detail || "Failed to load chunk score");
    } finally {
      setChunkLoading(false);
    }
  };

  const handleUpdateChunkScore = async () => {
    if (!chunkObjectId.trim()) {
      setChunkStatus("Please enter a MongoDB chunk id");
      return;
    }

    const parsedScore = Number(chunkExtraScore);
    if (Number.isNaN(parsedScore)) {
      setChunkStatus("Please enter a valid extra score");
      return;
    }

    setChunkLoading(true);
    setChunkStatus("");
    try {
      const data = await updateChunkExtraScore(chunkObjectId.trim(), parsedScore);
      setChunkPriority(data);
      setChunkExtraScore(data.extra_score ?? parsedScore);
      setChunkStatus("Extra score updated");
    } catch (err) {
      setChunkStatus(err.response?.data?.detail || "Failed to update extra score");
    } finally {
      setChunkLoading(false);
    }
  };

  const cancelRemove = () => {
    setConfirmModal({ isOpen: false, email: "" });
  };

  if (loading) return <div className="user-management">Loading...</div>;

  return (
    <div className="workspace">
      <Sidebar user={user} currentPage="users" onPageChange={onPageChange} onLogout={logLogout} onNavigation={logNavigation} />
      <main className="annotator">
        <div className="user-management">
          <h1>User Management</h1>

          {error && <div className="error">{error}</div>}

          <div className="user-section">
            <h2>Add New Annotator</h2>
            <form onSubmit={handleAddAnnotator} className="add-user-form">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter email address"
                required
              />
              <button type="submit">Add Annotator</button>
            </form>
          </div>

          <div className="user-section">
            <h2>Admins ({users.admins.length})</h2>
            <div className="user-list">
              {users.admins.map((admin) => (
                <div key={admin.email} className="user-item admin">
                  <span>{admin.email}</span>
                  <span className="role">Admin</span>
                </div>
              ))}
            </div>
          </div>

          <div className="user-section">
            <h2>Annotators ({users.annotators.length})</h2>
            <div className="user-list">
              {users.annotators.map((annotator) => (
                <div key={annotator.email} className="user-item">
                  <span>{annotator.email}</span>
                  <button
                    onClick={() => handleRemoveAnnotator(annotator.email)}
                    className="remove-btn"
                  >
                    Deactivate
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="user-section">
            <h2>Inactive Annotators ({users.inactive_annotators.length})</h2>
            <div className="user-list">
              {users.inactive_annotators.map((annotator) => (
                <div
                  key={annotator.email}
                  className="user-item"
                  style={{ opacity: 0.6 }}
                >
                  <span>{annotator.email}</span>
                  <button
                    onClick={() => handleReactivate(annotator.email)}
                    style={{
                      background: "#28a745",
                      color: "white",
                      border: "none",
                      padding: "4px 12px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "0.8rem",
                    }}
                  >
                    Reactivate
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="user-section">
            <h2>Chunk Priority Override</h2>
            <div className="add-user-form" style={{ gap: "8px", alignItems: "stretch" }}>
              <input
                type="text"
                value={chunkObjectId}
                onChange={(e) => setChunkObjectId(e.target.value)}
                placeholder="Enter MongoDB chunk _id"
              />
              <input
                type="number"
                step="0.1"
                value={chunkExtraScore}
                onChange={(e) => setChunkExtraScore(e.target.value)}
                placeholder="Extra score"
              />
              <button type="button" onClick={handleLoadChunkScore} disabled={chunkLoading}>
                Load Score
              </button>
              <button type="button" onClick={handleUpdateChunkScore} disabled={chunkLoading}>
                Update Extra Score
              </button>
            </div>
            {chunkStatus && <div className="error" style={{ marginTop: "10px" }}>{chunkStatus}</div>}
            {chunkPriority && (
              <div style={{ marginTop: "12px", lineHeight: "1.6" }}>
                <div><strong>chunk_id:</strong> {chunkPriority.chunk_id}</div>
                <div><strong>status:</strong> {chunkPriority.status}</div>
                <div><strong>divergence_score:</strong> {chunkPriority.divergence_score}</div>
                <div><strong>recency_score:</strong> {chunkPriority.recency_score}</div>
                <div><strong>extra_score:</strong> {chunkPriority.extra_score}</div>
                <div><strong>priority_score:</strong> {chunkPriority.priority_score}</div>
                <div><strong>priority:</strong> {chunkPriority.priority}</div>
              </div>
            )}
          </div>
        </div>
      </main>
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onConfirm={confirmRemove}
        onCancel={cancelRemove}
        title="Deactivate Annotator"
        message={`Are you sure you want to deactivate ${confirmModal.email}? They will not be able to login but their data will be preserved.`}
      />
    </div>
  );
}
